import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CollectionEditor, FormActions } from './CollectionEditor'
import { I18nField, SwitchField, TextField, type I18nValue } from './fields'
import { MediaField } from './MediaField'
import { useCollection } from '../useCollection'
import type { I18n, TeamMember } from '../types'

/**
 * L'équipe LeYAMO, telle qu'elle se présente au public.
 *
 * À ne pas confondre avec l'écran « Équipe » de l'administration, qui gère les
 * **comptes** et leurs droits : qui peut scanner une entrée, encaisser,
 * publier. Ici on gère des **visages** — beaucoup de gens de l'équipe n'auront
 * jamais de compte, et plusieurs comptes ne doivent jamais apparaître sur le
 * site. Les deux listes n'ont aucune raison de coïncider.
 *
 * Retirer quelqu'un de l'affichage se fait en décochant « visible », pas en
 * supprimant : son passage dans l'équipe n'a pas à disparaître parce qu'on ne
 * l'affiche plus cette année.
 */
export function TeamEditor({ eventId }: { eventId: string | undefined }) {
  const { t, i18n } = useTranslation()

  const members = useCollection<TeamMember>({
    key: 'admin-team-members',
    path: `/admin/events/${eventId}/team-members`,
    eventId,
    related: [['public-team']],
  })

  function label(value: I18n): string {
    return value[i18n.language] || value[Object.keys(value)[0] ?? ''] || ''
  }

  return (
    <CollectionEditor<TeamMember>
      title={t('admin.team.title')}
      description={t('admin.team.description')}
      items={members.items}
      busy={members.busy}
      error={members.error}
      emptyLabel={t('admin.team.empty')}
      createLabel={t('admin.team.add')}
      onDelete={(member) => members.remove.mutate({ id: member.id })}
      summarize={(member) => ({
        title: `${member.first_name} ${member.last_name}`.trim(),
        subtitle: label(member.role_i18n),
        dimmed: !member.is_visible,
      })}
      renderForm={(member, close) => (
        <MemberPanel
          member={member}
          eventId={eventId}
          busy={members.busy}
          onCancel={close}
          onSubmit={(payload) => {
            const done = { onSuccess: () => close() }
            if (member) members.update.mutate({ id: member.id, ...payload }, done)
            else members.create.mutate(payload, done)
          }}
        />
      )}
    />
  )
}

function MemberPanel({
  member,
  eventId,
  busy,
  onSubmit,
  onCancel,
}: {
  member: TeamMember | null
  eventId: string | undefined
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [firstName, setFirstName] = useState(member?.first_name ?? '')
  const [lastName, setLastName] = useState(member?.last_name ?? '')
  const [role, setRole] = useState<I18nValue>(member?.role_i18n ?? {})
  const [bio, setBio] = useState<I18nValue>(member?.bio_i18n ?? {})
  const [photo, setPhoto] = useState<string | null>(member?.photo_media_id ?? null)
  const [visible, setVisible] = useState(member?.is_visible ?? true)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label={t('ticketing.firstName')} value={firstName} onChange={setFirstName} />
        <TextField label={t('ticketing.lastName')} value={lastName} onChange={setLastName} />
        {/* Le rôle est traduit : « Cheffe de cuisine » n'a pas à s'afficher en
            français à un visiteur allemand. */}
        <I18nField label={t('admin.team.role')} value={role} onChange={setRole} />
        <I18nField label={t('admin.team.bio')} multiline value={bio} onChange={setBio} />
        <MediaField
          label={t('admin.team.photo')}
          hint={t('admin.team.photoHint')}
          value={photo}
          eventId={eventId}
          onChange={setPhoto}
        />
        <SwitchField label={t('admin.food.visible')} value={visible} onChange={setVisible} />
      </div>
      <FormActions
        busy={busy || !firstName.trim()}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            role_i18n: role,
            bio_i18n: bio,
            photo_media_id: photo,
            is_visible: visible,
          })
        }
      />
    </div>
  )
}
